import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle, ShieldCheck, PowerOff } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { track } from "@/lib/consent";
import {
  createSubmission,
  getConfig,
  getGuideRef,
  QUESTIONS,
  MIN_PER_QUESTION,
  MAX_TOTAL,
} from "@/lib/api";

const HINTS = [
  "Show genuine, informed interest in the subject — what sparked it and what you understand about studying it in the UK.",
  "Connect specific topics, projects or skills from your studies to what this course demands.",
  "Reflect on activities beyond school — what you did, what you learned, and why it matters for this course.",
];

export default function Submission() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [form, setForm] = useState({
    course: "",
    country: "",
    qualification_type: "",
    answer1: "",
    answer2: "",
    answer3: "",
  });

  useEffect(() => {
    getConfig()
      .then((c) => setAiEnabled(c.ai_enabled))
      .catch(() => setAiEnabled(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answers = useMemo(
    () => [form.answer1, form.answer2, form.answer3],
    [form.answer1, form.answer2, form.answer3]
  );
  const total = useMemo(
    () => answers.reduce((s, a) => s + a.length, 0),
    [answers]
  );
  const overTotal = total > MAX_TOTAL;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const eachMet = answers.every((a) => a.length >= MIN_PER_QUESTION);
  const canSubmit =
    form.course.trim() &&
    form.country.trim() &&
    form.qualification_type.trim() &&
    eachMet &&
    !overTotal &&
    agreed;

  const submit = async () => {
    if (!agreed) {
      toast.error("Please agree to the Privacy Policy and Terms of Service first.");
      return;
    }
    if (!canSubmit) {
      toast.error("Please complete every field and meet the character limits.");
      return;
    }
    setLoading(true);
    try {
      const res = await createSubmission({ ...form, guide_ref: getGuideRef() });
      track("submit", "/analyse");
      navigate(`/results/${res.id}`);
    } catch (e) {
      if (e?.response?.status === 503) setAiEnabled(false);
      toast.error(
        e?.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-sand">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 sm:px-8 py-12 sm:py-16">
        <div className="text-center">
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-ink">
            Analyse my statement
          </h1>
          <p className="mt-3 text-ink-soft leading-relaxed max-w-2xl mx-auto">
            Paste your answers to the three UCAS questions. Each answer needs at
            least {MIN_PER_QUESTION} characters, and all three together must stay
            within {MAX_TOTAL.toLocaleString()} characters.
          </p>

          <div
            className="mt-5 inline-flex items-start gap-2 rounded-xl bg-forest/[0.06] border border-forest/15 px-4 py-3 text-sm text-ink-soft text-left"
            data-testid="privacy-reassurance"
          >
            <ShieldCheck className="h-4 w-4 text-forest shrink-0 mt-0.5" strokeWidth={2} />
            Your statement is never stored — it's used to generate your feedback
            and then discarded.
          </div>
        </div>

        {/* Meta fields */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <div>
            <Label htmlFor="course" className="text-ink font-medium">
              Intended course / subject
            </Label>
            <Input
              id="course"
              data-testid="course-input"
              value={form.course}
              onChange={set("course")}
              placeholder="e.g. Economics"
              className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
            />
          </div>
          <div>
            <Label htmlFor="country" className="text-ink font-medium">
              Country where you earned your qualifications
            </Label>
            <Input
              id="country"
              data-testid="country-input"
              value={form.country}
              onChange={set("country")}
              placeholder="e.g. India"
              className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="qualification" className="text-ink font-medium">
              Qualification type
            </Label>
            <Input
              id="qualification"
              data-testid="qualification-input"
              value={form.qualification_type}
              onChange={set("qualification_type")}
              placeholder="e.g. A-levels, International Baccalaureate (IB), Indian CBSE, Chinese Gaokao, etc."
              className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
            />
            <p className="mt-2 text-sm text-ink-muted">
              Tell us the qualification you're applying with so feedback is tailored to it.
            </p>
          </div>
        </div>

        {/* Questions */}
        <div className="mt-12 space-y-10">
          {QUESTIONS.map((q, i) => {
            const val = answers[i];
            const met = val.length >= MIN_PER_QUESTION;
            return (
              <div
                key={q}
                className="grid gap-4 sm:grid-cols-3"
                data-testid={`question-block-${i + 1}`}
              >
                <div className="sm:col-span-1">
                  <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                    Question {i + 1}
                  </div>
                  <h3 className="mt-1 font-heading text-lg text-ink leading-snug">
                    {q}
                  </h3>
                  <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                    {HINTS[i]}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <textarea
                    data-testid={`q${i + 1}-textarea`}
                    value={val}
                    onChange={set(`answer${i + 1}`)}
                    placeholder="Write your answer here…"
                    className="w-full bg-white border border-border focus:border-clay focus:ring-1 focus:ring-clay rounded-xl p-6 text-base leading-relaxed transition-all resize-y min-h-[220px] outline-none"
                  />
                  <div
                    className="mt-2 flex items-center justify-end gap-1.5 text-sm"
                    data-testid={`q${i + 1}-counter`}
                  >
                    {met ? (
                      <CheckCircle2 className="h-4 w-4 text-forest" strokeWidth={2} />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-ink-faint" strokeWidth={2} />
                    )}
                    <span className={met ? "text-forest" : "text-ink-muted"}>
                      {val.length} / {MIN_PER_QUESTION} min
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sticky total bar */}
        <div className="sticky bottom-0 mt-12 -mx-6 sm:mx-0">
          <div className="bg-white/90 backdrop-blur border border-border rounded-none sm:rounded-2xl px-6 sm:px-8 py-5 shadow-[0_-8px_32px_rgba(17,42,34,0.05)]">
            {aiEnabled && (
              <label
                htmlFor="consent"
                className="flex items-start gap-3 mb-4 cursor-pointer"
                data-testid="consent-label"
              >
                <Checkbox
                  id="consent"
                  data-testid="consent-checkbox"
                  checked={agreed}
                  onCheckedChange={(v) => setAgreed(v === true)}
                  className="mt-0.5 data-[state=checked]:bg-clay data-[state=checked]:border-clay"
                />
                <span className="text-sm text-ink-soft leading-relaxed">
                  I agree to the{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-clay underline underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="text-clay underline underline-offset-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                  .
                </span>
              </label>
            )}
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div>
              <div
                className={`font-heading text-lg ${overTotal ? "text-clay" : "text-ink"}`}
                data-testid="total-counter"
              >
                {total.toLocaleString()} / {MAX_TOTAL.toLocaleString()} characters
              </div>
              <p className="text-sm text-ink-muted">
                {overTotal
                  ? "You're over the combined limit — trim your answers."
                  : "Combined across all three answers."}
              </p>
            </div>
            {aiEnabled ? (
              <Button
                data-testid="analyse-button"
                onClick={submit}
                disabled={!canSubmit || loading}
                className="bg-clay hover:bg-clay-hover text-white rounded-lg px-8 py-6 text-base font-medium disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analysing…
                  </>
                ) : (
                  "Analyse my statement"
                )}
              </Button>
            ) : (
              <div
                data-testid="tool-offline"
                className="inline-flex items-center gap-2 rounded-lg bg-sand-deep border border-border px-6 py-4 text-ink-muted font-medium"
              >
                <PowerOff className="h-4 w-4" strokeWidth={2} />
                Tool Offline
              </div>
            )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
